import { ACCESS_TOKEN_KEY } from '@/constants/app'
import { ApiError, apiClient, unwrap, LONG_RUNNING_TIMEOUT_MS } from '@/lib/apiClient'
import { downloadBlob, filenameFromDisposition } from '@/utils/csv'

/**
 * The SSE stream is read with `fetch`, which does not go through the axios
 * instance and so does not inherit its `baseURL`. Same source of truth, one line
 * apart, so the two cannot drift.
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

/**
 * Every API call in the application lives here as a plain exported function.
 * Components never call these directly — they go through TanStack Query
 * (`useQuery` / `useMutation`) so caching, retries and loading state are handled
 * in one consistent way.
 *
 * Endpoint contract, confirmed against the running API except where marked.
 *
 *   POST /admin/auth/login       { email, password } -> { user, access_token }
 *   GET  /admin/auth/validation                      -> { user, access_token }
 *   POST /admin/auth/logout
 *
 *   Every dashboard route takes ?from&to&granularity&type and is mounted on its
 *   own base, not under /pipeline.
 *   GET  /dashboard             -> { summary, leads, bot, interviews, recruiters,
 *                                    pitch, outcomes, trends, live }
 *   GET  /dashboard/leads       -> { leads }        intake by source
 *   GET  /dashboard/bot         -> { bot }          conversation lifecycle
 *   GET  /dashboard/interviews  -> { interviews }   booked / confirmed / held
 *   GET  /dashboard/recruiters  -> { recruiters: { recruiters, totals } }
 *   GET  /dashboard/pitch       -> { pitch: { managers, totals } }
 *   GET  /dashboard/outcomes    -> { outcomes }     hired + every reason
 *   GET  /dashboard/trends      -> { trends }       the window as a series
 *   GET  /dashboard/live        -> { live }         snapshot, ignores the range
 *   (admin and managers only; every other role gets a 403)
 *
 *   GET    /pipeline/recruiter/metrics?from&to&granularity&type&recruiterId
 *                                       -> { recruiter, metrics }  (own figures
 *                                          for a recruiter; managers name one)
 *
 *   GET    /pipeline/:type/queue?…     -> { applicants: [...], total }
 *   GET    /pipeline/:type/queue/job-titles -> { jobTitles: [{ jobTitle, count }] }
 *   GET    /pipeline/:type/:id          -> applicant with its workflow
 *   PATCH  /pipeline/:type/:id/status   { status, ...stage fields }
 *   POST   /pipeline/:type/:id/reassign { recruiterId, note }  (ops)
 *   POST   /pipeline/:type/:id/resume   multipart file -> { resumeUrl }  (pipeline roles)
 *   GET    /pipeline/:type/hired?page&limit&sortBy&sortDirection&from&to&search&jobTitle
 *                                       -> { hired: [...], total, totals: { csr, other },
 *                                            jobTitles: [{ jobTitle, count }],
 *                                            page, totalPages }
 *                                          `hiredAt` is DERIVED from the history entry
 *                                          that set `hired`; from/to filter on it and are
 *                                          YYYY-MM-DD read in Asia/Karachi. Scoped by whose
 *                                          hire it was, not by stage.
 *   GET    /pipeline/walk-in/context    -> { type, manager }  (recruiter only)
 *   POST   /pipeline/walk-in            { name, phone, … } -> { type, applicant }
 *                                       409 + { existing } when already in the pipeline
 *   POST   /pipeline/import/preview     multipart file -> headers + sample rows
 *   POST   /pipeline/import?type=        multipart file + mapping JSON
 *
 *   The interview calendar. Reads are open to every pipeline role; a recruiter is
 *   confined to their own calendar by the server whatever they ask for.
 *   GET    /interviews?from&to&ownerId&type&status
 *                                       -> { interviews: [{ …slot, ownerId: { username },
 *                                            applicant: { name, phone, workflow } | null }] }
 *                                          `from`/`to` are YYYY-MM-DD read in Asia/Karachi;
 *                                          `status` is a comma list, default "booked"
 *   GET    /interviews/capacity?leadType&day -> { capacity: [{ ownerId, username, cap,
 *                                            taken, remaining }] }
 *   POST   /interviews                  { type, applicantId, ownerId, startsAt, endsAt }
 *   POST   /interviews/:id/cancel       { reason? }   (admin + manager)
 *
 *   Meta Lead Ads. Reads need admin/manager/marketer, writes admin/manager.
 *   Every reporting route takes ?from&to (YYYY-MM-DD, the ad account's timezone)
 *   and ?refresh=true to bypass the server's read cache.
 *   GET    /meta/status                 -> { configured, webhookConfigured, missing: [env keys],
 *                                            pageId, adAccountId, account: { currency, timezoneName } }
 *                                          answers even when unconfigured — never 503
 *   GET    /meta/campaigns              -> { campaigns, totals, currency, timezoneName, range, partial }
 *   GET    /meta/campaigns/:id/adsets   -> { adsets, totals, … }
 *   GET    /meta/adsets/:id/ads         -> { ads, totals, … }
 *   GET    /meta/forms                  -> { forms: [{ id, name, status, leadsCount, questions,
 *                                            staged: {new,imported,ignored,duplicate,error,total},
 *                                            lastLeadAt, lastSyncedAt, lastSyncError }] }
 *   GET    /meta/leads?formId&status&from&to&search&page&limit
 *                                       -> { leads, total, page, totalPages, counts }
 *                                          staging only — works even when Meta is unreachable
 *   GET    /meta/leads/export/summary?from&to&status -> { forms: [{ formId, formName,
 *                                          count, firstLeadAt, lastLeadAt, ads: [...] }], total }
 *   GET    /meta/leads/export?formIds&adKeys&status&from&to -> text/csv (streamed download;
 *                                          no selection = everything in range)
 *   GET    /meta/leads/:id              -> { lead }  incl. fieldData + raw payload
 *   PATCH  /meta/leads/:id/status       { status: "new"|"ignored", note? }
 *   POST   /meta/sync                   { formIds?, since? } -> { summary: { forms, totals } }
 *   POST   /meta/cache/clear            -> drops the cached campaign/insight reads
 *
 *   WhatsApp Cloud API. Reads need any pipeline role; sending is stage owners
 *   only — admin is read-only here, as it is for pipeline status changes.
 *   GET    /whatsapp/status             -> { configured, webhookConfigured, callingConfigured,
 *                                            missing: [env keys], numbers, phoneNumbers,
 *                                            streamListeners }
 *                                          answers even when unconfigured — never 503
 *   GET    /whatsapp/templates          -> { templates: [{ name, language, category,
 *                                            body, parameterCount }] }  APPROVED only, cached
 *   GET    /whatsapp/threads?department&unread&page&limit
 *                                       -> { threads: [{ …conversation, applicant,
 *                                            serviceWindowOpen }], total }
 *   GET    /whatsapp/threads/:type/:applicantId?before
 *                                       -> { conversation, messages, serviceWindowOpen, canSend }
 *                                          `conversation` is null until somebody messages
 *   POST   /whatsapp/threads/:type/:applicantId/messages
 *                                       { text } | { templateName, templateLanguage?,
 *                                                    templateParams? }
 *                                       422 when the 24h window is shut and no template given
 *   POST   /whatsapp/threads/:type/:applicantId/read   -> { unreadCount: 0 }
 *   POST   /whatsapp/messages/:id/resume  save an inbound attachment as the CV
 *   GET    /whatsapp/messages/:id/media   the attachment's bytes, proxied from Meta
 *   GET    /whatsapp/stream             -> SSE. Read with fetch, NOT EventSource —
 *                                          EventSource cannot send an Authorization header.
 *
 *   GET    /s3/signed-url?key=orjobs/…  -> { data: { key, url } }  url expires in 1h
 *
 *   GET    /screening/meta              -> { result: { questionTypes, closingStatuses, … } }
 *   GET    /screening/configs?department -> { configs: [...] }
 *   POST   /screening/configs           { name, department, questions, … }
 *   PUT    /screening/configs/:id       partial update
 *   DELETE /screening/configs/:id
 *   POST   /screening/names/backfill   { dryRun } -> what the screening renames
 *   GET    /screening/bot/sessions      -> { sessions: [...] }
 *   GET    /screening/bot/dashboard     -> { live, funnel, rates, scripts, series }
 *   GET    /screening/bot/live          -> { total, stale, byState, sessions }
 *   GET    /screening/bot/broadcast/preview -> { budget, willSend, moreWaiting, scripts }
 *   POST   /screening/bot/broadcast     { type, configId, limit }
 *   GET    /screening/autostart         -> { configs: { [scriptId]: {...} } }
 *   PUT    /screening/autostart/:id     { autoStart, dailyLimit }
 *   DELETE /screening/autostart/:id
 *   POST   /screening/autostart/:id/drain  (or /autostart/drain for all)
 *   GET    /screening/templates         -> { templates: [...] }
 *   POST   /screening/templates/sync    mirror the list from Meta
 *   PUT    /screening/templates/:id     { label, departments, variables } only
 *
 *   GET    /roles                       -> { result: { roles: [...] } }
 *   GET    /user?role=&leadType=        -> { users: [...] }
 *   POST   /user                        { username, email, password, role, ... }
 *   PUT    /user/:id                    partial update (no password — see below)
 *   DELETE /user/:id
 *
 *   GET    /listings/                 -> { result: { joblisting: [...] } }
 *   GET    /listings/byId/:id         -> { result: { joblisting: {...} } }
 *   POST   /listings/                 -> created listing
 *   PUT    /listings/:id              -> updated listing
 *   PATCH  /listings/:id/toggle-status
 *   (no DELETE route exists — deactivating is how a listing is retired)
 *
 *   TODO(api): not deployed yet
 *   GET  /jobs?page&pageSize&sort&dir
 */

/* ------------------------------------------------------------------ auth --- */

export const login = (credentials) => apiClient.post('/admin/auth/login', credentials).then(unwrap)

/** Exchanges a stored token for the current user; used on cold app start. */
export const validateSession = () => apiClient.get('/admin/auth/validation').then(unwrap)

export const logout = () => apiClient.post('/admin/auth/logout').then(() => true)

/* ------------------------------------------------------------- dashboard --- */

/**
 * Reporting, for administrators and managers only — every route below answers
 * 403 to any other role.
 *
 * The responses are flat (`{ success, range, leads }`, …) rather than wrapped in
 * `result`, so `unwrap` passes the whole body through and each function returns
 * the one key its caller asked for.
 *
 * `from`/`to` are `YYYY-MM-DD` read in Asia/Karachi; omitting them asks for the
 * server's own default of the last 30 days. `granularity` sizes the trend
 * buckets; `type` narrows the WHOLE response to one applicant collection.
 *
 * Everything answers inside the window except `live`, which is a snapshot and
 * says so.
 */
const dashboardParams = ({ from, to, granularity, type }) => ({
  ...(from ? { from } : {}),
  ...(to ? { to } : {}),
  ...(granularity ? { granularity } : {}),
  ...(type ? { type } : {}),
})

const section = (path, key) => ({ from, to, granularity, type, signal } = {}) =>
  apiClient
    .get(`/dashboard${path}`, {
      params: dashboardParams({ from, to, granularity, type }),
      signal,
    })
    .then(unwrap)
    .then((body) => body?.[key] ?? null)

/** Every section in one request — the page's only call in normal use. */
export const getDashboard = ({ from, to, granularity, type, signal } = {}) =>
  apiClient
    .get('/dashboard', {
      params: dashboardParams({ from, to, granularity, type }),
      signal,
    })
    .then(unwrap)

/** Intake by source and department, plus what Meta staged and what was imported. */
export const getDashboardLeads = section('/leads', 'leads')

/** The bot: conversations opened, where they got to, and how the leads resolved. */
export const getDashboardBot = section('/bot', 'bot')

/** Interviews booked, confirmed, held and cancelled, and what is still ahead. */
export const getDashboardInterviews = section('/interviews', 'interviews')

/**
 * One row per recruiter: what they did, what became of their leads, and the
 * hires-over-time series the same card draws.
 *
 * The only section fetcher that keeps the whole body rather than one key: the
 * table and the chart under it are one card and must answer one window, so the
 * server sends both together and the card takes both.
 */
export const getDashboardRecruiters = ({ from, to, granularity, type, signal } = {}) =>
  apiClient
    .get('/dashboard/recruiters', {
      params: dashboardParams({ from, to, granularity, type }),
      signal,
    })
    .then(unwrap)
    .then((body) => ({
      recruiters: body?.recruiters ?? null,
      hiresOverTime: body?.hiresOverTime ?? null,
    }))

/** The support desk: leads reviewed after the bot, confirm calls, and the backlog. */
export const getDashboardSupport = section('/support', 'support')

/**
 * One person's own figures — the only dashboard route a recruiter may call.
 *
 * `recruiterId` is honoured for an administrator or a manager and IGNORED by the
 * server for a recruiter, who always reads themselves. The response carries a
 * `mode` (`recruiter` | `support`) saying which layout the payload is for; the
 * client must branch on that rather than re-deriving it from the session, which
 * does not carry the availability config the rule reads.
 */
export const getMyPerformance = ({
  from,
  to,
  granularity,
  type,
  recruiterId,
  signal,
} = {}) =>
  apiClient
    .get('/dashboard/me', {
      params: {
        ...dashboardParams({ from, to, granularity, type }),
        ...(recruiterId ? { recruiterId } : {}),
      },
      signal,
    })
    .then(unwrap)

/** The final pitch, per manager. */
export const getDashboardPitch = section('/pitch', 'pitch')

/** Hired, and every not-hired reason with its own count. */
export const getDashboardOutcomes = section('/outcomes', 'outcomes')

/** The window as a time series, bucketed by `granularity`. */
export const getDashboardTrends = section('/trends', 'trends')

/** Where the pipeline stands right now. Deliberately not range-scoped. */
export const getDashboardLive = section('/live', 'live')

/* ---------------------------------------------------------- job listings --- */

/**
 * The listings endpoint returns the whole collection in one call and offers no
 * server-side search, sort or pagination — the list view derives those on the
 * client. `unwrap` peels the `result` envelope; the payload nests one level
 * further under `joblisting`.
 */
export const getJobListings = ({ signal } = {}) =>
  apiClient
    .get('/listings/', { signal })
    .then(unwrap)
    .then((result) => result?.joblisting ?? [])

export const getJobListing = (listingId, { signal } = {}) =>
  apiClient
    .get(`/listings/byId/${listingId}`, { signal })
    .then(unwrap)
    .then((result) => result?.joblisting ?? result)

export const createJobListing = (payload) => apiClient.post('/listings/', payload).then(unwrap)

export const updateJobListing = ({ listingId, payload }) =>
  apiClient.put(`/listings/${listingId}`, payload).then(unwrap)

/**
 * Applicants who came in through job listings, across both pipelines, narrowed
 * by the listings' department / lead type / id and by applied date. Admins and
 * managers only — it carries contact details.
 */
export const getListingLeads = ({ jobId, department, leadType, from, to, signal } = {}) =>
  apiClient
    .get('/listings/leads', {
      params: {
        jobId: jobId || undefined,
        department: department || undefined,
        leadType: leadType || undefined,
        from: from || undefined,
        to: to || undefined,
      },
      signal,
    })
    .then(unwrap)
    .then((result) => ({
      leads: result?.leads ?? [],
      total: result?.total ?? 0,
      truncated: Boolean(result?.truncated),
    }))

/** Flips Active <-> Inactive. There is no delete route; this retires a listing. */
export const toggleJobListingStatus = (listingId) =>
  apiClient.patch(`/listings/${listingId}/toggle-status`).then(unwrap)

/* ------------------------------------------------------------------ blogs --- */

/**
 * Careers-site blog posts (admins and content writers). The post travels in the
 * stored shape both ways; the server normalises it and answers a rejected save
 * with `errors: ['sections[0].blocks[1].text: …']`, which arrives on the
 * ApiError as `fieldErrors` — the editor marks each field from that list.
 * The public read the careers site uses is `/blogs/public/:slug`, not called here.
 */
export const getBlogPosts = (params = {}, { signal } = {}) =>
  apiClient.get('/blogs', { params, signal }).then((response) => response.data)

export const getBlogPost = (postId, { signal } = {}) =>
  apiClient.get(`/blogs/${postId}`, { signal }).then((response) => response.data.post)

export const createBlogPost = (payload) =>
  apiClient.post('/blogs', payload).then((response) => response.data.post)

export const updateBlogPost = ({ postId, payload }) =>
  apiClient.put(`/blogs/${postId}`, payload).then((response) => response.data.post)

/** status: 'draft' | 'published' | 'archived'. Publishing runs the completeness check. */
export const setBlogPostStatus = ({ postId, status }) =>
  apiClient.patch(`/blogs/${postId}/status`, { status }).then((response) => response.data.post)

export const deleteBlogPost = (postId) => apiClient.delete(`/blogs/${postId}`)

/** Counts for the metric cards: `{ total, draft, published, archived, categories, lastPublishedAt }`. */
export const getBlogStats = ({ signal } = {}) =>
  apiClient.get('/blogs/stats', { signal }).then((response) => response.data.stats)

export const getBlogCategories = ({ signal } = {}) =>
  apiClient.get('/blogs/categories', { signal }).then((response) => response.data.categories)

/** Uploads to the public blog folder. Returns `{ src, width, height }`. */
export const uploadBlogImage = (file) => {
  const form = new FormData()
  form.append('file', file)
  return apiClient
    .post('/blogs/upload-image', form, { timeout: LONG_RUNNING_TIMEOUT_MS })
    .then((response) => response.data.image)
}

/* ------------------------------------------------------- hiring pipeline --- */

/**
 * One page of the hiring queue.
 *
 * Applicants are split across two collections; `type` is `csr`, `other`, or
 * `all` to read both as a single queue — the server unions them before
 * de-duplicating, sorting and paginating, so the page numbers are real.
 *
 * The queue auto-filters by the caller: it returns only the applicants at the
 * caller's own stage that are assigned to them. `stage` is an ops-view override
 * that only administrators and managers may pass.
 *
 * Search, filtering and sorting are all server-side. `search` matches name,
 * email or phone; `name` / `email` / `phone` target one field each. Phone terms
 * are matched on their national digits, so "0300…" finds a lead imported as
 * "+92300…". `sortBy` is one of name, appliedAt, assignedAt, status,
 * lastUpdatedAt, interviewAt.
 *
 * Rows come back tagged with `_type`, which every follow-up call is scoped by.
 */
export const getPipelineQueue = ({ type = 'all', signal, ...filters } = {}) =>
  apiClient
    .get(`/pipeline/${type}/queue`, { params: filters, signal })
    .then(unwrap)
    .then((result) => ({
      applicants: result?.applicants ?? [],
      total: result?.total ?? result?.applicants?.length ?? 0,
      totalPages: result?.totalPages ?? 1,
    }))

/**
 * The roles present in the caller's own queue, each with a count, for the
 * job-title filter.
 *
 * There is no canonical list of job titles — they come in as free text with each
 * CSV import — so the options are read from the queue itself. A csr queue answers
 * with an empty list, because those leads are recorded without a title; the
 * filter is simply not offered there.
 *
 * `stage` is passed for the same reason the queue takes it: an ops viewer looking
 * at one stage should be offered the titles at that stage.
 */
export const getQueueJobTitles = ({ type = 'all', stage, view, signal } = {}) =>
  apiClient
    .get(`/pipeline/${type}/queue/job-titles`, { params: { stage, view }, signal })
    .then(unwrap)
    .then((result) => result?.jobTitles ?? [])

/**
 * Who holds the leads in the caller's queue, each with a count, for the
 * recruiter filter. Scoped like the job titles — administrators and managers
 * only; a recruiter's queue is their own.
 */
export const getQueueRecruiters = ({ type = 'all', stage, view, signal } = {}) =>
  apiClient
    .get(`/pipeline/${type}/queue/recruiters`, { params: { stage, view }, signal })
    .then(unwrap)
    .then((result) => result?.recruiters ?? [])

/**
 * One recruiter's own figures: how many leads were assigned to them per
 * day/week/month over the range, and what those leads are doing now
 * (`byStatus`, `byStage`), plus `currentLoad` — their live recruiter-stage queue.
 *
 * A recruiter always gets their own numbers and `recruiterId` is ignored for
 * them. Managers and administrators must pass one; a manager only for a
 * recruiter on their own team. `from`/`to` default to the server's last 30 days;
 * `all` drops the window and counts every lead ever assigned to them.
 */
export const getRecruiterMetrics = ({
  recruiterId,
  from,
  to,
  granularity,
  type,
  all,
  signal,
} = {}) =>
  apiClient
    .get('/pipeline/recruiter/metrics', {
      params: {
        // With `all` the dates are meaningless — sending them would only give
        // the server a window to honour instead of ignoring.
        ...(all ? {} : dashboardParams({ from, to })),
        ...(granularity ? { granularity } : {}),
        ...(all ? { all: 'true' } : {}),
        ...(recruiterId ? { recruiterId } : {}),
        ...(type ? { type } : {}),
      },
      signal,
    })
    .then(unwrap)

export const getPipelineApplicant = ({ type, applicantId, signal }) =>
  apiClient.get(`/pipeline/${type}/${applicantId}`, { signal }).then(unwrap)

/** `payload` carries the new status plus whatever fields that stage requires. */
export const updatePipelineStatus = ({ type, applicantId, payload }) =>
  apiClient.patch(`/pipeline/${type}/${applicantId}/status`, payload).then(unwrap)

/**
 * Attaches (or replaces) an applicant's CV. Multipart, field name `file`.
 *
 * The file goes to the API, not to S3 from here: the server uploads it to the
 * private prefix and writes the resulting key onto the applicant, so the browser
 * never holds bucket credentials and cannot point a record at an arbitrary
 * object. Recruiters and managers only — admin is
 * read-only. Returns the new object key.
 */
export const uploadApplicantResume = ({ type, applicantId, file }) => {
  const form = new FormData()
  form.append('file', file)
  return apiClient
    .post(`/pipeline/${type}/${applicantId}/resume`, form)
    .then(unwrap)
    .then((result) => result?.resumeUrl ?? result?.applicant?.resumeUrl ?? null)
}

/* ---------------------------------------------------------------- walk-in --- */

/**
 * Which department the signed-in recruiter records walk-ins into.
 *
 * Read from the server rather than derived here, so one rule decides it: the
 * department on the caller's own config. It also decides which fields the form
 * asks for (`csr` stores a nightshift preference), and somebody with no
 * department gets the refusal now rather than on submit, with a candidate
 * standing in front of them.
 */
export const getWalkInContext = ({ signal } = {}) =>
  apiClient.get('/pipeline/walk-in/context', { signal }).then(unwrap)

/**
 * Records a candidate who turned up in person. Recruiter-only.
 *
 * Neither the applicant type nor the owner is sent: the server takes both from
 * the calling recruiter's team, so a walk-in always lands in that recruiter's own
 * queue at the recruiter stage, and they screen and schedule it with the ordinary
 * stage actions.
 *
 * An open lead for the same phone or email is refused with a 409 whose
 * `details` carries that record. Re-sending with `force: true` creates the walk-in
 * anyway, for a genuine second person on a shared number.
 */
export const createWalkIn = (payload) => apiClient.post('/pipeline/walk-in', payload).then(unwrap)

/**
 * Moves a recruiter-stage lead to a different recruiter on the same type's team.
 * Requires the lead-assignment grant. Rejects with 409 if the lead has left the recruiter stage.
 */
export const reassignPipelineLead = ({ type, applicantId, recruiterId, note }) =>
  apiClient.post(`/pipeline/${type}/${applicantId}/reassign`, { recruiterId, note }).then(unwrap)

/**
 * Deletes a lead and everything that points at it — interviews, bot sessions,
 * the WhatsApp transcript — and frees any staged Meta lead to be imported again.
 *
 * Administrators only, and irreversible. For records that should not exist: test
 * rows, duplicates, an erasure request. A lead that was worked and finished is
 * closed with an outcome instead, which takes it off the queue and keeps the
 * history.
 */
export const deletePipelineLead = ({ type, applicantId }) =>
  apiClient.delete(`/pipeline/${type}/${applicantId}`).then(unwrap)

/**
 * Attaches or clears the screening script the bot runs for one existing lead.
 *
 * A website application inherits its script from the listing and an import gets
 * one per batch, so this is for leads that arrived before their listing had a
 * script — nothing else can ever pick those up, because a broadcast only messages
 * leads with a script already stored.
 *
 * `screeningConfigId: null` is a real answer meaning "work this by hand". Sending
 * nothing at all is refused, so a forgotten field cannot quietly unhook a lead.
 */
export const setLeadScreeningScript = ({ type, applicantId, screeningConfigId }) =>
  apiClient
    .put(`/pipeline/${type}/${applicantId}/screening-script`, {
      screeningConfigId: screeningConfigId || null,
    })
    .then(unwrap)

/**
 * Claim a lead in the department's shared piles — the bot's handovers, and the
 * interviews nobody has rung yet.
 *
 * Nothing about the lead changes. It says "I am ringing this one" to the rest of
 * the department, so two recruiters do not call the same candidate. Rejects with
 * 409 whose message names whoever is already holding it.
 */
export const claimPipelineLead = ({ type, applicantId }) =>
  apiClient.post(`/pipeline/${type}/${applicantId}/claim`).then(unwrap)

/**
 * Give a claimed lead back before its lease runs out. An admin or manager
 * releasing somebody else's claim is a force-release, decided server-side from
 * the caller's role.
 */
export const releasePipelineLead = ({ type, applicantId }) =>
  apiClient.post(`/pipeline/${type}/${applicantId}/claim/release`).then(unwrap)

/* ------------------------------------------------------- lead CSV import --- */

/**
 * Step 1 — parse a CSV without writing anything, so the column mapper can be
 * built from the file's real headers. Multipart, field name `file`.
 *
 * `suggested` only covers the fields the server auto-detects for the mapper:
 * `name`, `phone` and the optional dedup `id`. Email and city are still
 * alias-detected at import time even when the mapping leaves them out.
 */
export const previewLeadImport = (file) => {
  const form = new FormData()
  form.append('file', file)
  return apiClient.post('/pipeline/import/preview', form).then(unwrap)
}

/**
 * Step 2 — commit the mapped file. `type` goes on the query string; the body
 * carries the file and `mapping` as a JSON string.
 *
 * Mapping keys hold *original header names*: `name` and `phone` are required,
 * `email`/`city` optional, `others` is a list of extra headers to keep, and `id`
 * is the dedup column — sending `id: ''` explicitly switches deduplication back
 * to email-or-phone even when the file has an id column.
 *
 * `screeningConfigId` is required by the API and may be null, which means the
 * batch is worked by hand.
 */
export const importLeads = ({ type, file, mapping, jobTitle, screeningConfigId }) => {
  const form = new FormData()
  form.append('file', file)
  form.append('mapping', JSON.stringify(mapping))
  if (jobTitle) form.append('jobTitle', jobTitle)
  // Always appended, including as an empty string — that is how "work these by
  // hand" travels through multipart, and the API distinguishes it from a batch
  // that named no script at all, which it refuses.
  form.append('screeningConfigId', screeningConfigId ?? '')
  return apiClient
    .post('/pipeline/import', form, { params: { type } })
    .then(unwrap)
    .then((result) => result?.summary ?? result)
}

/* --------------------------------------------------------------- meta ads --- */

/**
 * Reporting window, as the two `YYYY-MM-DD` parameters the endpoints read. Blank
 * ends are omitted rather than sent empty — the server defaults an absent range to
 * the last 30 days, and an empty string would look like a deliberate one.
 */
const metaRange = ({ from, to } = {}) => ({
  ...(from ? { from } : {}),
  ...(to ? { to } : {}),
})

/**
 * Whether the integration is usable, and against which page and ad account.
 *
 * The one Meta endpoint that answers even when nothing is configured — it is what
 * the page reads to decide between rendering itself and explaining what ops has
 * to set. `missing` names the blank env keys.
 */
export const getMetaStatus = ({ signal } = {}) =>
  apiClient.get('/meta/status', { signal }).then(unwrap)

/**
 * Campaigns with their performance over the window.
 *
 * `totals` is the server's, not a sum of `rows`: cost per lead is a ratio, so it
 * has to be total spend over total leads rather than an average of the column.
 * `partial` means pagination or a rate limit cut the walk short and the figures
 * under-report.
 *
 * The three levels return the same `{ rows, totals, currency }` shape so one table
 * and one summariser serve all of them.
 */
export const getMetaCampaigns = ({ from, to, status, refresh, signal } = {}) =>
  apiClient
    .get('/meta/campaigns', {
      params: { ...metaRange({ from, to }), status, ...(refresh ? { refresh: true } : {}) },
      signal,
    })
    .then(unwrap)
    .then((result) => ({
      rows: result?.campaigns ?? [],
      totals: result?.totals ?? null,
      currency: result?.currency ?? null,
      timezoneName: result?.timezoneName ?? null,
      partial: Boolean(result?.partial),
    }))

export const getMetaAdSets = ({ campaignId, from, to, refresh, signal }) =>
  apiClient
    .get(`/meta/campaigns/${campaignId}/adsets`, {
      params: { ...metaRange({ from, to }), ...(refresh ? { refresh: true } : {}) },
      signal,
    })
    .then(unwrap)
    .then((result) => ({
      rows: result?.adsets ?? [],
      totals: result?.totals ?? null,
      currency: result?.currency ?? null,
      timezoneName: result?.timezoneName ?? null,
      partial: Boolean(result?.partial),
    }))

export const getMetaAds = ({ adSetId, from, to, refresh, signal }) =>
  apiClient
    .get(`/meta/adsets/${adSetId}/ads`, {
      params: { ...metaRange({ from, to }), ...(refresh ? { refresh: true } : {}) },
      signal,
    })
    .then(unwrap)
    .then((result) => ({
      rows: result?.ads ?? [],
      totals: result?.totals ?? null,
      currency: result?.currency ?? null,
      timezoneName: result?.timezoneName ?? null,
      partial: Boolean(result?.partial),
    }))

/**
 * The page's lead forms, each with what we hold for it.
 *
 * `leadsCount` is Meta's lifetime figure and `staged` is what has reached us — they
 * legitimately disagree (our lookback window is finite, and Meta excludes test leads
 * from its own count), so both are shown rather than reconciled.
 */
export const getMetaForms = ({ refresh, signal } = {}) =>
  apiClient
    .get('/meta/forms', { params: refresh ? { refresh: true } : {}, signal })
    .then(unwrap)
    .then((result) => result?.forms ?? [])

/**
 * What a lead form does with its leads: which pipeline they join, the job title
 * they carry, the bot's script, and how many conversations a day it may open.
 *
 * Whole-object, not a patch — the six fields are edited together in one drawer,
 * and a partial update would make "clear the script" indistinguishable from
 * "leave the script alone".
 *
 * The response carries a `warning` for the one problem that does not block the
 * save: a script whose opening template Meta has not approved. The settings are
 * legitimately prepared before approval lands.
 */
export const saveMetaFormConfig = ({ formId, ...config }) =>
  apiClient.put(`/meta/forms/${formId}/config`, config).then(unwrap)

/** Back to being worked by hand: leads still stage, nothing is promoted. */
export const deleteMetaFormConfig = ({ formId }) =>
  apiClient.delete(`/meta/forms/${formId}/config`).then(unwrap)

/**
 * Open this form's waiting leads now, up to what is left of today's allowance.
 *
 * The manual pair to the daily drain — for verifying a setup without waiting a
 * day, and for releasing a backlog deliberately after raising the limit.
 */
export const drainMetaForm = ({ formId }) =>
  apiClient
    .post(`/meta/forms/${formId}/drain`, {}, { timeout: LONG_RUNNING_TIMEOUT_MS })
    .then(unwrap)

/**
 * Staged leads — pulled from Meta but not yet applicants.
 *
 * Server-side filtering and paging, like the hiring queue. `counts` is the status
 * breakdown of the filtered set ignoring the status filter itself, so the tabs can
 * show what switching to another status would find.
 */
export const getMetaLeads = ({ formId, status, from, to, search, page, limit, signal } = {}) =>
  apiClient
    .get('/meta/leads', {
      params: {
        ...(formId ? { formId } : {}),
        ...(status ? { status } : {}),
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(search ? { search } : {}),
        page,
        limit,
      },
      signal,
    })
    .then(unwrap)
    .then((result) => ({
      leads: result?.leads ?? [],
      total: result?.total ?? 0,
      page: result?.page ?? 1,
      totalPages: result?.totalPages ?? 1,
      counts: result?.counts ?? {},
    }))

/** One staged lead in full, including the raw Meta payload the list omits. */
export const getMetaLead = (leadId, { signal } = {}) =>
  apiClient
    .get(`/meta/leads/${leadId}`, { signal })
    .then(unwrap)
    .then((result) => result?.lead ?? null)

/** Ignore a staged lead, or put it back in the queue. */
export const setMetaLeadStatus = ({ leadId, status, note }) =>
  apiClient
    .patch(`/meta/leads/${leadId}/status`, { status, note })
    .then(unwrap)
    .then((result) => result?.lead ?? null)

/**
 * The export page's cards: every form with a lead in the range, its ads broken out,
 * and the count each would export. Scoped by the same range (with time) and optional
 * status the download uses, so a card's number is exactly what its file will hold.
 */
export const getMetaExportSummary = ({ from, to, status, signal } = {}) =>
  apiClient
    .get('/meta/leads/export/summary', {
      params: { ...metaRange({ from, to }), ...(status ? { status } : {}) },
      signal,
    })
    .then(unwrap)
    .then((result) => ({ forms: result?.forms ?? [], total: result?.total ?? 0 }))

/**
 * Download the staged leads as a CSV.
 *
 * `responseType: 'blob'` because the body is a file, not the JSON envelope the rest
 * of this module unwraps. An empty `formIds`/`adKeys` selection means "everything in
 * range" — the server reads no selection as download-all. The server names the file
 * (dated, range-stamped) via `Content-Disposition`; we honour that, falling back to a
 * plain name if a proxy strips the header.
 *
 * The long timeout matches the sync: a big export is a long single request, and
 * aborting it loses a download the server has already spent the query building.
 */
export const downloadMetaLeadsExport = async ({ formIds, adKeys, status, from, to } = {}) => {
  const response = await apiClient.get('/meta/leads/export', {
    params: {
      ...metaRange({ from, to }),
      ...(status ? { status } : {}),
      ...(formIds?.length ? { formIds: formIds.join(',') } : {}),
      ...(adKeys?.length ? { adKeys: adKeys.join(',') } : {}),
    },
    responseType: 'blob',
    timeout: LONG_RUNNING_TIMEOUT_MS,
  })
  const filename =
    filenameFromDisposition(response.headers?.['content-disposition']) ?? 'meta-leads.csv'
  downloadBlob(filename, response.data)
  return { filename }
}

/**
 * Promote staged leads into the pipeline.
 *
 * Two payload variants, one endpoint: an explicit id list, or a whole form. `leadIds`
 * wins when present so a stale `formId` in component state can never widen an explicit
 * selection into "everything on that form".
 *
 * Returns the same summary shape the CSV import returns, plus `duplicates` — the people
 * skipped because they are already in the pipeline by another route.
 */
export const importMetaLeads = ({
  type,
  jobTitle,
  leadIds,
  formId,
  from,
  to,
  force,
  screeningConfigId,
}) =>
  apiClient
    .post(
      '/meta/import',
      {
        type,
        jobTitle,
        // Sent unconditionally, INCLUDING as null. `null` is the explicit "work
        // these by hand" answer; the API refuses a batch that names no script at
        // all, so a conditional spread here would look like a missing decision.
        screeningConfigId: screeningConfigId ?? null,
        ...(force ? { force: true } : {}),
        ...(leadIds?.length ? { leadIds } : { formId, ...metaRange({ from, to }) }),
      },
      // Promoting a batch writes a lead at a time and may start a bot
      // conversation for each. Same reasoning as the sync: it must not be
      // abandoned halfway through by a stopwatch meant for reads.
      { timeout: LONG_RUNNING_TIMEOUT_MS },
    )
    .then(unwrap)
    .then((result) => result?.summary ?? result)

/**
 * Pull recent leads from Meta now. `formIds` narrows it to specific forms; omitted,
 * it walks every form on the page from each one's own watermark.
 */
export const runMetaSync = ({ formIds, since } = {}) =>
  apiClient
    .post(
      '/meta/sync',
      {
        ...(formIds?.length ? { formIds } : {}),
        ...(since ? { since } : {}),
      },
      // A full sync walks every form on the page, one Graph call each, and runs
      // past the default timeout with nothing new to stage. Aborting it here
      // cancelled the report, not the sync — the leads landed and the button
      // still said it failed.
      { timeout: LONG_RUNNING_TIMEOUT_MS },
    )
    .then(unwrap)
    .then((result) => result?.summary ?? null)

/* --------------------------------------------------------------- whatsapp --- */

/**
 * Configuration and health. Answers even when nothing is set up, so the inbox can
 * explain itself rather than showing an empty list.
 *
 * `phoneNumbers[].messaging_limit_tier` is the figure that decides what this
 * integration can do. It is portfolio-wide rather than per number — Meta shares
 * one limit across every number in the business portfolio — and it has to reach
 * TIER_2 (2,000 unique recipients per 24h) before calling can be enabled at all.
 */
export const getWhatsappStatus = ({ refresh, signal } = {}) =>
  apiClient
    .get('/whatsapp/status', { params: refresh ? { refresh: true } : {}, signal })
    .then(unwrap)

/**
 * Approved message templates.
 *
 * Only APPROVED ones come back: a PENDING or REJECTED template in the picker is a
 * send that will fail, so it is not offered. `parameterCount` is how many `{{n}}`
 * values the composer must collect before it can send.
 */
export const getWhatsappTemplates = ({ refresh, signal } = {}) =>
  apiClient
    .get('/whatsapp/templates', { params: refresh ? { refresh: true } : {}, signal })
    .then(unwrap)
    .then((result) => result?.templates ?? [])

/**
 * The inbox: one row per conversation, scoped by lead ownership rather than by
 * department. Unread first, then most recently active.
 */
export const getWhatsappThreads = ({ department, unread, search, page, limit, signal } = {}) =>
  apiClient
    .get('/whatsapp/threads', {
      params: {
        ...(department ? { department } : {}),
        ...(unread ? { unread: 'true' } : {}),
        ...(search ? { search } : {}),
        page,
        limit,
      },
      signal,
    })
    .then(unwrap)
    .then((result) => ({ threads: result?.threads ?? [], total: result?.total ?? 0 }))

/**
 * One transcript.
 *
 * `conversation: null` is the normal state of a lead nobody has messaged — not an
 * error. `serviceWindowOpen` says whether free-form text is allowed right now;
 * when it is false only an approved template will send.
 */
export const getWhatsappThread = ({ type, applicantId, before, signal }) =>
  apiClient
    .get(`/whatsapp/threads/${type}/${applicantId}`, {
      params: before ? { before } : {},
      signal,
    })
    .then(unwrap)
    .then((result) => ({
      conversation: result?.conversation ?? null,
      messages: result?.messages ?? [],
      serviceWindowOpen: Boolean(result?.serviceWindowOpen),
      canSend: Boolean(result?.canSend),
    }))

/**
 * Send. `{ text }` for free-form, which is only legal inside the 24-hour window;
 * `{ templateName, … }` to open a conversation or to reopen a closed window.
 */
export const sendWhatsappMessage = ({
  type,
  applicantId,
  text,
  templateName,
  templateLanguage,
  templateParams,
}) =>
  apiClient
    .post(`/whatsapp/threads/${type}/${applicantId}/messages`, {
      ...(templateName
        ? { templateName, templateLanguage, templateParams: templateParams ?? [] }
        : { text }),
    })
    .then(unwrap)

/**
 * Save an inbound attachment as the lead's CV.
 *
 * Deliberate rather than automatic: candidates send screenshots and ID photos as
 * well as CVs, and only the person reading the thread can tell which is which.
 */
export const saveWhatsappAttachmentAsResume = ({ messageId }) =>
  apiClient.post(`/whatsapp/messages/${messageId}/resume`).then(unwrap)

/**
 * An attachment's bytes, as a Blob, for the inbox to play or open.
 *
 * Fetched through axios rather than set as an `<audio src>`: the API authenticates
 * with a bearer header, which a media element cannot send. The error body comes
 * back as a Blob too, so a refusal is read here — otherwise "Meta keeps media for
 * about 30 days" would reach the user as a bare status message. A 401 is left to
 * the interceptor, which ends the session.
 */
export const getWhatsappMedia = async ({ messageId, signal }) => {
  const response = await apiClient.get(`/whatsapp/messages/${messageId}/media`, {
    responseType: 'blob',
    headers: { Accept: '*/*' },
    timeout: LONG_RUNNING_TIMEOUT_MS,
    validateStatus: (status) => status < 500 && status !== 401,
    signal,
  })

  if (response.status >= 300) {
    let body = null
    try {
      body = JSON.parse(await response.data.text())
    } catch {
      // Not JSON: fall through to the generic message.
    }
    throw new ApiError({
      message: body?.error ?? body?.msg ?? body?.message ?? 'Could not load that attachment.',
      status: response.status,
    })
  }

  return response.data
}

/** Clears the unread count and sends the lead their blue ticks. */
export const markWhatsappThreadRead = ({ type, applicantId }) =>
  apiClient.post(`/whatsapp/threads/${type}/${applicantId}/read`).then(unwrap)

/**
 * Subscribe to live inbox events.
 *
 * Read with `fetch` rather than `EventSource`, which cannot set headers — this API
 * authenticates with a bearer token, and the alternative (a token in the query
 * string) would put a JWT into every access log between here and the server.
 *
 * The trade-off is that reconnection is ours to do; the caller owns that, because
 * only it knows whether the component is still mounted. Returns an abort function.
 */
export const streamWhatsappEvents = ({ onEvent, onError, signal }) => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY)
  const controller = new AbortController()
  if (signal) signal.addEventListener('abort', () => controller.abort(), { once: true })

  const run = async () => {
    const response = await fetch(`${API_BASE_URL}/whatsapp/stream`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), Accept: 'text/event-stream' },
      signal: controller.signal,
    })
    if (!response.ok || !response.body) throw new Error(`stream failed: ${response.status}`)

    const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
    let buffer = ''

    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += value

      // SSE frames are separated by a blank line. Anything after the last one is
      // a partial frame and stays in the buffer for the next chunk — a message
      // split across two TCP reads is the normal case, not an edge case.
      const frames = buffer.split('\n\n')
      buffer = frames.pop() ?? ''

      for (const frame of frames) {
        const data = frame
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trim())
          .join('')
        if (!data) continue // keep-alive comment lines carry none
        try {
          onEvent(JSON.parse(data))
        } catch {
          // A frame we cannot parse is not worth tearing the stream down for.
        }
      }
    }
  }

  run().catch((error) => {
    if (controller.signal.aborted) return
    onError?.(error)
  })

  return () => controller.abort()
}

/* --------------------------------------------------------- private files --- */

/**
 * Resumes live in a private S3 prefix (`orjobs/resume/…`) and an applicant only
 * stores the object key, not a URL — the bucket refuses anonymous reads, so the
 * key has to be exchanged for a presigned link before anything can open it.
 *
 * The link is short-lived (the server signs for one hour), which is why this is
 * fetched per view rather than resolved once at import time.
 */
export const getSignedFileUrl = (key, { signal } = {}) =>
  apiClient
    .get('/s3/signed-url', { params: { key }, signal })
    .then(unwrap)
    .then((result) => result?.url ?? null)

/* ------------------------------------------------------- users and roles --- */

/** The fixed, backend-owned role list. Read-only: roles are not editable. */
/**
 * The vocabulary the user form is built from: the roles, the grantable route
 * catalogue, and the per-role defaults.
 *
 * Returns the whole payload rather than just the roles. The catalogue used to be
 * duplicated on the client, and it drifted — the WhatsApp inbox was grantable on
 * the server and invisible in the form, so nobody could be given it. One source
 * cannot drift.
 */
export const getRoles = ({ signal } = {}) =>
  apiClient
    .get('/roles', { signal })
    .then(unwrap)
    .then((result) => ({
      roles: result?.roles ?? [],
      routes: result?.routes ?? [],
      defaultRoutes: result?.defaultRoutes ?? {},
      configFields: result?.configFields ?? {},
    }))

/**
 * Users, newest first. Flat records — recruiters report to nobody, so there is no
 * team to populate. Optional filters: `role`, `leadType` (the department).
 */
export const getUsers = ({ role, leadType, signal } = {}) =>
  apiClient
    .get('/user', { params: { role, leadType }, signal })
    .then(unwrap)
    .then((result) => result?.users ?? (Array.isArray(result) ? result : []))

export const createUser = (payload) => apiClient.post('/user', payload).then(unwrap)

/**
 * The hires, newest first, with a count per department beside them.
 *
 * `type` is a department or `all`. Who appears is the server's decision and a
 * different rule from the queue's: a hire has left every working stage, so it is
 * scoped by whose hire it was — a recruiter sees the ones they worked, a manager
 * their departments', an administrator everything.
 *
 * `totals` counts every department the caller may read, over the same date range
 * as the rows, so the strip above the table and the table itself always agree.
 */
export const getHiredLeads = ({
  type = 'all',
  page,
  limit,
  sortBy,
  sortDirection,
  from,
  to,
  search,
  jobTitle,
  recruiterId,
  signal,
} = {}) =>
  apiClient
    .get(`/pipeline/${type}/hired`, {
      params: {
        page,
        limit,
        sortBy,
        sortDirection,
        from,
        to,
        search,
        jobTitle,
        // Ignored by the server for a recruiter, who is already pinned to their
        // own hires — so it can only ever narrow, never widen.
        recruiterId,
      },
      signal,
    })
    .then(unwrap)

/* ---------------------------------------------------------- interviews --- */

/**
 * Booked interviews in a window, each with its candidate attached.
 *
 * `from`/`to` are `YYYY-MM-DD` read in Asia/Karachi, so a week asked for is the
 * week the team means wherever the browser is set. `type` narrows to one
 * department; `status` is a comma list and defaults to `booked` server-side —
 * `held` is worth asking for when the question is what the bot is offering right
 * now, and `released` when it is what was cancelled.
 *
 * Scope is the server's to decide: a recruiter always gets their own calendar and
 * nothing else, whatever `ownerId` says.
 */
export const getInterviews = ({ from, to, ownerId, type, status, signal } = {}) =>
  apiClient
    .get('/interviews', { params: { from, to, ownerId, type, status }, signal })
    .then(unwrap)
    .then((result) => result?.interviews ?? [])

/** Per-person cap, taken and remaining for one Karachi day. */
export const getInterviewCapacity = ({ leadType, day, signal } = {}) =>
  apiClient
    .get('/interviews/capacity', { params: { leadType, day }, signal })
    .then(unwrap)
    .then((result) => result?.capacity ?? [])

/**
 * The next open interview times, each already attributed to the interviewer whose
 * calendar would take it.
 *
 * The same list the bot offers a candidate, which is the point: a recruiter
 * booking by hand and the bot booking automatically compete for one set of hours,
 * so they have to be choosing from one source. `day` narrows to a single Karachi
 * day; without it the window is `days` forward from now.
 */
/**
 * `perOwner` lists every free interviewer at every free time rather than one per
 * time. The dashboard's picker wants it — a recruiter rescheduling is choosing
 * for the whole department — while the bot wants the collapsed shape, because a
 * candidate picks a time and must never be offered the same one twice.
 */
export const getInterviewAvailability = ({
  leadType,
  day,
  days,
  limit,
  ownerId,
  perOwner,
  signal,
} = {}) =>
  apiClient
    .get('/interviews/availability', {
      params: { leadType, day, days, limit, ownerId, perOwner: perOwner || undefined },
      signal,
    })
    .then(unwrap)
    .then((result) => result?.slots ?? [])

/** Only the days with something still open — so picking one is never a dead end. */
export const getInterviewAvailableDays = ({ leadType, days, perOwner, signal } = {}) =>
  apiClient
    .get('/interviews/availability/days', {
      params: { leadType, days, perOwner: perOwner || undefined },
      signal,
    })
    .then(unwrap)
    .then((result) => result?.days ?? [])

/**
 * Book an interview into a specific open slot.
 *
 * This is the only way to schedule one. The booking IS the assignment: the lead
 * moves to whoever's calendar took the slot, its review flag clears, and the
 * history records the note. Sending a time as a plain status change instead would
 * write the lead's `scheduledAt` and leave the calendar untouched, which the API
 * now refuses.
 *
 * A lead that already has an interview gives the old slot back as part of the
 * same call, so rescheduling is this endpoint too.
 */
export const bookInterview = ({ type, applicantId, ownerId, startsAt, endsAt, note }) =>
  apiClient
    .post('/interviews', { type, applicantId, ownerId, startsAt, endsAt, note })
    .then(unwrap)

/** Release a booked slot back to the pool. Admin and managers. */
export const cancelInterview = ({ interviewId, reason }) =>
  apiClient.post(`/interviews/${interviewId}/cancel`, { reason }).then(unwrap)

/* ----------------------------------------------------------- screening --- */

/**
 * The screening editor's vocabulary — question types, answer outcomes, the
 * closing statuses an answer may use, and WhatsApp's option limits.
 *
 * Fetched rather than hardcoded: the server validates against exactly this list,
 * and a client copy would drift the first time a closing status is added.
 */
export const getScreeningMeta = ({ signal } = {}) =>
  apiClient
    .get('/screening/meta', { signal })
    .then(unwrap)
    .then((result) => result ?? {})

/** Screening scripts. `department` and `status` narrow the list server-side. */
export const getScreeningConfigs = ({ department, status, jobTitle, signal } = {}) =>
  apiClient
    .get('/screening/configs', { params: { department, status, jobTitle }, signal })
    .then(unwrap)
    .then((result) => result?.configs ?? [])

export const getScreeningConfig = ({ configId, signal } = {}) =>
  apiClient
    .get(`/screening/configs/${configId}`, { signal })
    .then(unwrap)
    .then((result) => result?.config ?? null)

export const createScreeningConfig = (payload) =>
  apiClient.post('/screening/configs', payload).then(unwrap)

export const updateScreeningConfig = ({ configId, payload }) =>
  apiClient.put(`/screening/configs/${configId}`, payload).then(unwrap)

export const deleteScreeningConfig = (configId) =>
  apiClient.delete(`/screening/configs/${configId}`).then(unwrap)

/**
 * Apply the name each already-screened candidate typed to the lead it is stored
 * on — the catch-up for everyone screened before the bot started writing it
 * through.
 *
 * `dryRun` defaults to true on the server and has to be turned off deliberately:
 * this renames leads in bulk, so the first call is always a rehearsal whose
 * result is a list to read.
 */
export const backfillCandidateNames = ({ types, dryRun = true, limit } = {}) =>
  apiClient
    .post('/screening/names/backfill', { types, dryRun, limit })
    .then(unwrap)
    .then((result) => result?.result ?? null)

/**
 * What the bot asked a lead, and what came back.
 *
 * Readable by any pipeline role: a recruiter handed a bot-screened lead has to
 * see the answers it acted on, and a lead the bot CLOSED is the case where that
 * matters most.
 */
export const getBotSessions = ({ type, applicantId, status, signal } = {}) =>
  apiClient
    .get('/screening/bot/sessions', { params: { type, applicantId, status }, signal })
    .then(unwrap)
    .then((result) => result?.sessions ?? [])

/**
 * Everything the bot page reads, in one request: the live board, the funnel over
 * the window, and conversations opened per day.
 */
export const getBotDashboard = ({ from, to, type, signal } = {}) =>
  apiClient
    .get('/screening/bot/dashboard', { params: { from, to, type }, signal })
    .then(unwrap)

/**
 * The live board on its own, for polling. Separate from the dashboard so
 * refreshing "what is happening right now" does not recompute a month's funnel
 * every few seconds.
 */
export const getBotLive = ({ type, signal } = {}) =>
  apiClient.get('/screening/bot/live', { params: { type }, signal }).then(unwrap)

/**
 * What a broadcast would do, and running it.
 *
 * Two calls because a broadcast is not undoable: every conversation it opens is a
 * message a real person receives, and it spends a portfolio-wide daily allowance
 * that nothing can give back.
 */
export const getBroadcastPreview = ({ type, configId, limit, signal } = {}) =>
  apiClient
    .get('/screening/bot/broadcast/preview', { params: { type, configId, limit }, signal })
    .then(unwrap)

export const runBroadcast = ({ type, configId, limit }) =>
  apiClient.post('/screening/bot/broadcast', { type, configId, limit }).then(unwrap)

/**
 * Auto-start: whether a script opens its own careers-site leads on a schedule.
 *
 * The careers-site counterpart to a Meta form's settings. Returned keyed by
 * script id, because the caller already holds the scripts and is joining onto
 * them — a list would only be turned into this map on arrival.
 */
export const getAutoStartConfigs = ({ signal } = {}) =>
  apiClient
    .get('/screening/autostart', { signal })
    .then(unwrap)
    .then((result) => result?.configs ?? {})

/**
 * Whole-object, like the Meta form drawer: two fields edited together, and a
 * partial update would make "off" indistinguishable from "unchanged".
 *
 * `leadType` is deliberately not sent — the server takes it from the script's own
 * department, so the two can never disagree.
 */
export const saveAutoStartConfig = ({ screeningConfigId, autoStart, dailyLimit }) =>
  apiClient
    .put(`/screening/autostart/${screeningConfigId}`, { autoStart, dailyLimit })
    .then(unwrap)

export const deleteAutoStartConfig = (screeningConfigId) =>
  apiClient.delete(`/screening/autostart/${screeningConfigId}`).then(unwrap)

/**
 * Run the drain now, for one script or for all of them.
 *
 * The manual pair to the daily cron: a first run should be watched, not waited
 * for, and this is how somebody watches it.
 */
export const runAutoStartDrain = ({ screeningConfigId } = {}) =>
  apiClient
    .post(
      screeningConfigId
        ? `/screening/autostart/${screeningConfigId}/drain`
        : '/screening/autostart/drain',
    )
    .then(unwrap)

/**
 * The WhatsApp templates a conversation can open with.
 *
 * Mirrored from Meta, which owns them: they are authored and approved in
 * WhatsApp Manager, so `approvalState` here is a reading rather than a claim.
 * There is no create and no delete — a new template is a Manager task.
 */
export const getMessageTemplates = ({ department, status, signal } = {}) =>
  apiClient
    .get('/screening/templates', { params: { department, status }, signal })
    .then(unwrap)
    .then((result) => result?.templates ?? [])

/** Refresh the mirror. Returns a summary, including any approved-but-unmapped. */
export const syncMessageTemplates = () =>
  apiClient.post('/screening/templates/sync').then(unwrap)

/**
 * The variable mapping, the label and the departments — the only parts of a
 * template that are ours. Everything else is overwritten by the next sync.
 */
export const updateMessageTemplate = ({ templateId, payload }) =>
  apiClient.put(`/screening/templates/${templateId}`, payload).then(unwrap)

/**
 * Partial update.
 *
 * Deliberately never sends `password`: this endpoint writes the body straight to
 * the document, so a password sent here would be stored unhashed. Password is
 * set once, at creation, where the server hashes it.
 */
export const updateUser = ({ userId, payload }) =>
  apiClient.put(`/user/${userId}`, payload).then(unwrap)


export const deleteUser = (userId) => apiClient.delete(`/user/${userId}`).then(unwrap)

/**
 * Query key factory. Keys live beside the functions they describe so a cache
 * entry can always be traced back to the call that fills it.
 */
export const queryKeys = {
  dashboard: {
    all: ['dashboard'],
    overview: (params) => ['dashboard', 'overview', params],
    leads: (params) => ['dashboard', 'leads', params],
    bot: (params) => ['dashboard', 'bot', params],
    interviews: (params) => ['dashboard', 'interviews', params],
    recruiters: (params) => ['dashboard', 'recruiters', params],
    support: (params) => ['dashboard', 'support', params],
    me: (params) => ['dashboard', 'me', params ?? {}],
    pitch: (params) => ['dashboard', 'pitch', params],
    outcomes: (params) => ['dashboard', 'outcomes', params],
    trends: (params) => ['dashboard', 'trends', params],
    live: (params) => ['dashboard', 'live', params],
  },
  jobListings: {
    all: ['jobListings'],
    // No params in the key: the endpoint always returns the full collection and
    // the view filters it, so one cache entry serves every filter combination.
    list: () => ['jobListings', 'list'],
    detail: (listingId) => ['jobListings', 'detail', listingId],
    leads: (params) => ['jobListings', 'leads', params ?? {}],
  },
  blogs: {
    all: ['blogs'],
    list: (params) => ['blogs', 'list', params ?? {}],
    detail: (postId) => ['blogs', 'detail', postId],
    categories: () => ['blogs', 'categories'],
    stats: () => ['blogs', 'stats'],
  },
  interviews: {
    all: ['interviews'],
    // Keyed on the whole window+filters object: the calendar asks for one week at
    // a time, so stepping back to last week is a cache hit rather than a refetch.
    calendar: (params) => ['interviews', 'calendar', params ?? {}],
    capacity: (params) => ['interviews', 'capacity', params ?? {}],
    availability: (params) => ['interviews', 'availability', params ?? {}],
    availableDays: (params) => ['interviews', 'availableDays', params ?? {}],
  },
  pipeline: {
    all: ['pipeline'],
    queue: (type, filters) => ['pipeline', 'queue', type, filters],
    queueJobTitles: (type, stage, view) => [
      'pipeline',
      'queue',
      type,
      'jobTitles',
      stage ?? null,
      view ?? null,
    ],
    queueRecruiters: (type, stage, view) => [
      'pipeline',
      'queue',
      type,
      'recruiters',
      stage ?? null,
      view ?? null,
    ],
    applicant: (type, applicantId) => ['pipeline', 'applicant', type, applicantId],
    recruiterMetrics: (params) => ['pipeline', 'recruiterMetrics', params ?? {}],
    hired: (type, filters) => ['pipeline', 'hired', type, filters ?? {}],
    walkInContext: () => ['pipeline', 'walkInContext'],
  },
  meta: {
    all: ['meta'],
    status: () => ['meta', 'status'],
    // Drill levels nest under the campaign id, so invalidating
    // ['meta','campaigns',id] drops one campaign's whole subtree.
    campaigns: (range) => ['meta', 'campaigns', range ?? {}],
    adSets: (campaignId, range) => ['meta', 'campaigns', campaignId, 'adsets', range ?? {}],
    ads: (adSetId, range) => ['meta', 'adsets', adSetId, 'ads', range ?? {}],
    forms: () => ['meta', 'forms'],
    leads: (filters) => ['meta', 'leads', filters ?? {}],
    exportSummary: (filters) => ['meta', 'export-summary', filters ?? {}],
  },
  whatsapp: {
    all: ['whatsapp'],
    status: () => ['whatsapp', 'status'],
    // No params: the template list is small, identical for every caller, and
    // already cached server-side.
    templates: () => ['whatsapp', 'templates'],
    threads: (filters) => ['whatsapp', 'threads', filters ?? {}],
    // Nested under the applicant so a live event for one thread can invalidate
    // exactly that thread and nothing else.
    thread: (type, applicantId) => ['whatsapp', 'thread', type, applicantId],
  },
  files: {
    all: ['files'],
    signedUrl: (key) => ['files', 'signedUrl', key],
  },
  users: {
    all: ['users'],
    list: (filters) => ['users', 'list', filters ?? {}],
    roles: () => ['users', 'roles'],
  },
  screening: {
    all: ['screening'],
    meta: () => ['screening', 'meta'],
    configs: (filters) => ['screening', 'configs', filters ?? {}],
    config: (configId) => ['screening', 'configs', configId],
    templates: (filters) => ['screening', 'templates', filters ?? {}],
    botSessions: (filters) => ['screening', 'bot-sessions', filters ?? {}],
    botDashboard: (filters) => ['screening', 'bot-dashboard', filters ?? {}],
    botLive: (filters) => ['screening', 'bot-live', filters ?? {}],
    broadcastPreview: (filters) => ['screening', 'broadcast-preview', filters ?? {}],
    autoStart: () => ['screening', 'autostart'],
  },
}
